
import React from "react";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Ban } from "lucide-react";
import { BookingType } from "@/lib/supabase/types";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

interface PaymentStatusBadgeProps {
  booking: BookingType;
  onUpdatePaymentStatus?: (bookingId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => void;
}

const PaymentStatusBadge: React.FC<PaymentStatusBadgeProps> = ({ 
  booking, 
  onUpdatePaymentStatus 
}) => {
  const { t } = useTranslation();

  if (!onUpdatePaymentStatus) {
    return (
      <>
        {booking.paymentStatus === 'paid' && (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <DollarSign className="h-3 w-3 mr-1" />
            {t('admin.payment.paid')}
          </Badge>
        )}
        {booking.paymentStatus === 'pending' && (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            {t('admin.payment.pending')}
          </Badge>
        )}
        {booking.paymentStatus === 'nptr' && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                  <Ban className="h-3 w-3 mr-1" />
                  {t('admin.payment.nptr')}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('admin.payment.nptrTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {booking.paymentStatus === 'paid' && (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <DollarSign className="h-3 w-3 mr-1" />
          {t('admin.payment.paid')}
        </Badge>
      )}
      {booking.paymentStatus === 'pending' && (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          {t('admin.payment.pending')}
        </Badge>
      )}
      {booking.paymentStatus === 'nptr' && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                <Ban className="h-3 w-3 mr-1" />
                {t('admin.payment.nptr')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('admin.payment.nptrTooltip')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 px-2">
            {t('admin.payment.changeStatus')} <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>{t('admin.payment.changeStatus')}</DropdownMenuLabel>
          {booking.paymentStatus !== 'paid' && (
            <DropdownMenuItem onClick={() => onUpdatePaymentStatus(booking.id, 'paid')}>
              <DollarSign className="h-4 w-4 mr-2" />
              {t('admin.payment.markAsPaid')}
            </DropdownMenuItem>
          )}
          {booking.paymentStatus !== 'pending' && (
            <DropdownMenuItem onClick={() => onUpdatePaymentStatus(booking.id, 'pending')}>
              {t('admin.payment.markAsPending')}
            </DropdownMenuItem>
          )}
          {booking.paymentStatus !== 'nptr' && (
            <DropdownMenuItem onClick={() => onUpdatePaymentStatus(booking.id, 'nptr')}>
              <Ban className="h-4 w-4 mr-2" />
              {t('admin.payment.markAsNPTR')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default PaymentStatusBadge;
